/**
 * Cloud Error Reporting — the second sink, and the one that keeps stacks.
 *
 * This is not a transport. Transports receive `WireEvent`s, which are flat,
 * redacted, and stack-free by design; Cloud Error Reporting groups and
 * deduplicates on the *stack trace*, so handing it a sanitized event would
 * turn every distinct crash into one indistinguishable blob.
 *
 * So the two sinks are fed from the same call sites with different payloads:
 * `web_error` (category + short message) goes to the dashboards for counting,
 * and the real `Error` object comes here for grouping and alerting. Neither
 * is a substitute for the other, and the split is why `web_error` can afford
 * to carry so little.
 *
 * Operational, not behavioural — no consent gate applies, and it initializes
 * in production only.
 *
 * @module logging/error-reporting
 */

import StackdriverErrorReporter from 'stackdriver-errors-js';
import { diagnostic } from './core/diagnostic';

let reporter: StackdriverErrorReporter | null = null;

/**
 * Start Cloud Error Reporting.
 *
 * Also hooks `window.onerror` and `window.onunhandledrejection` internally,
 * which is why `instrumentation/errors.ts` only has to worry about emitting
 * the matching telemetry event rather than capturing the stack itself.
 *
 * Safe to call in any environment; only activates in production, where
 * `vite.config.ts` guarantees the required keys exist (the build aborts if
 * they do not).
 */
export function initErrorReporting(): void {
  if (!import.meta.env.PROD) {
    diagnostic('error reporting skipped (dev)');
    return;
  }

  try {
    reporter = new StackdriverErrorReporter();
    reporter.start({
      key: import.meta.env.VITE_GCP_ERROR_REPORTING_API_KEY,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      service: 'homeceo-web',
    });
    diagnostic('error reporting initialized');
  } catch (error) {
    diagnostic('error reporting failed to initialize', error);
  }
}

/**
 * Report an error, preserving its stack.
 *
 * A no-op outside production. Never throws — reporting a failure is not
 * permitted to cause one.
 */
export function reportError(error: Error): void {
  if (!reporter) {
    diagnostic('error not reported (no reporter)', error.message);
    return;
  }
  try {
    reporter.report(error).catch(() => {
      // Best-effort. A failed report is not worth a console error, which
      // would fail the e2e suite for every route that logged one.
    });
  } catch (failure) {
    diagnostic('error reporting threw', failure);
  }
}
